/**
 * @description
 * Time-series service for VSCode Lens data aggregation and snapshots.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type {
  TimeSeriesAggregation,
  DomainSnapshot,
  DomainAggregation,
  DomainStatistics,
  SnapshotComparison,
  VSCodeLensTable,
} from "../../../types/vscode-lens";

/**
 * Time-series service for VSCode Lens data
 */
export class TimeSeriesService extends BaseAPIClient {
  /**
   * Get time-series aggregation for a table
   */
  async getTimeSeries(
    table: VSCodeLensTable,
    granularity: "hour" | "day",
    emailColumn: string,
    startTime?: number,
    endTime?: number,
  ): Promise<TimeSeriesAggregation> {
    const params = new URLSearchParams();
    params.set("granularity", granularity);
    params.set("emailColumn", emailColumn);

    if (startTime) params.set("startTime", startTime.toString());
    if (endTime) params.set("endTime", endTime.toString());

    const data = await this.request<{ aggregation: TimeSeriesAggregation }>(
      `/vscode-lens/timeseries/${table}?${params.toString()}`,
    );
    return data.aggregation;
  }

  /**
   * Get historical snapshots for a table
   */
  async getSnapshots(
    table: VSCodeLensTable,
    options?: {
      start?: number;
      end?: number;
      limit?: number;
      offset?: number;
    },
  ): Promise<{
    snapshots: DomainSnapshot[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const params = new URLSearchParams();

    if (options?.start) params.set("start", options.start.toString());
    if (options?.end) params.set("end", options.end.toString());
    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.offset) params.set("offset", options.offset.toString());

    return this.request<{
      snapshots: DomainSnapshot[];
      total: number;
      limit: number;
      offset: number;
    }>(`/vscode-lens/timeseries/snapshots/${table}?${params.toString()}`);
  }

  /**
   * Create a manual snapshot
   */
  async createSnapshot(
    table: VSCodeLensTable,
    aggregation: DomainAggregation,
    statistics: DomainStatistics,
    notes?: string,
  ): Promise<DomainSnapshot> {
    const data = await this.request<{ snapshot: DomainSnapshot }>(
      `/vscode-lens/timeseries/snapshots/${table}`,
      {
        method: "POST",
        body: JSON.stringify({ aggregation, statistics, notes }),
      },
    );
    return data.snapshot;
  }

  /**
   * Get a specific snapshot by ID
   */
  async getSnapshotById(
    table: VSCodeLensTable,
    id: string,
  ): Promise<DomainSnapshot> {
    const data = await this.request<{ snapshot: DomainSnapshot }>(
      `/vscode-lens/timeseries/snapshots/${table}/${id}`,
    );
    return data.snapshot;
  }

  /**
   * Compare two snapshots
   */
  async compareSnapshots(
    snapshot1Id: string,
    snapshot2Id: string,
  ): Promise<SnapshotComparison> {
    const data = await this.request<{ comparison: SnapshotComparison }>(
      "/vscode-lens/timeseries/snapshots/compare",
      {
        method: "POST",
        body: JSON.stringify({ snapshot1Id, snapshot2Id }),
      },
    );
    return data.comparison;
  }

  /**
   * Delete old snapshots
   */
  async cleanupSnapshots(
    retentionDays: number = 90,
  ): Promise<{ deletedCount: number }> {
    return this.request<{
      deletedCount: number;
      message: string;
      retentionDays: number;
    }>(
      `/vscode-lens/timeseries/snapshots/cleanup?retentionDays=${retentionDays}`,
      { method: "DELETE" },
    );
  }
}

// Export singleton instance
export const timeSeriesService = new TimeSeriesService();
